import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

interface NO3SignedFormsTableData {
  id: string;
  [key: string]: any;
}

interface TableResponse {
  content: NO3SignedFormsTableData[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

interface UploadResponse {
  success: boolean;
  message: string;
  data?: any;
}

interface UseNO3SignedFormsParams {
  inboundPpo?: string;
  outboundPpo?: string;
  page?: number;
  size?: number;
}

export function useNO3SignedForms({ inboundPpo, outboundPpo, page = 0, size = 10 }: UseNO3SignedFormsParams = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File): Promise<UploadResponse> => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post('/signatures/no3', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch table data after successful upload
      queryClient.invalidateQueries({ queryKey: ['no3-inbound'] });
      queryClient.invalidateQueries({ queryKey: ['no3-outbound'] });
      setUploadError(null);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('upload.error', 'Upload failed');
      setUploadError(errorMessage);
    },
  });

  // Inbound table data query
  const {
    data: inboundData,
    isLoading: inboundLoading,
    error: inboundError,
    refetch: refetchInbound,
  } = useQuery<TableResponse>({
    queryKey: ['no3-inbound', { inboundPpo, page, size }],
    queryFn: async () => {
      const params = new URLSearchParams();
      
      if (inboundPpo) params.append('inboundPpo', inboundPpo);
      params.append('page', page.toString());
      params.append('size', size.toString());

      const response = await axios.get(`/signatures/no3/inbound-ppo?${params.toString()}`);
      return response.data;
    },
    enabled: !!inboundPpo, // Only fetch when inboundPpo is provided
  });

  // Outbound table data query
  const {
    data: outboundData,
    isLoading: outboundLoading,
    error: outboundError,
    refetch: refetchOutbound,
  } = useQuery<TableResponse>({
    queryKey: ['no3-outbound', { outboundPpo, page, size }],
    queryFn: async () => {
      const params = new URLSearchParams();
      
      if (outboundPpo) params.append('outboundPpo', outboundPpo);
      params.append('page', page.toString());
      params.append('size', size.toString());

      const response = await axios.get(`/signatures/no3/outbound-ppo?${params.toString()}`);
      return response.data;
    },
    enabled: !!outboundPpo, // Only fetch when outboundPpo is provided
  });

  // Upload file handler
  const handleUpload = useCallback(async (file: File): Promise<boolean> => {
    try {
      await uploadMutation.mutateAsync(file);
      return true;
    } catch (error) {
      return false;
    }
  }, [uploadMutation]);

  // Refresh all data
  const refreshData = useCallback(() => {
    refetchInbound();
    refetchOutbound();
  }, [refetchInbound, refetchOutbound]);

  return {
    // Upload
    handleUpload,
    isUploading: uploadMutation.isPending,
    uploadError,
    uploadSuccess: uploadMutation.isSuccess,
    
    // Inbound data
    inboundData: inboundData?.content || [],
    inboundTotalCount: inboundData?.totalElements || 0,
    inboundLoading,
    inboundError,
    refetchInbound,
    
    // Outbound data
    outboundData: outboundData?.content || [],
    outboundTotalCount: outboundData?.totalElements || 0,
    outboundLoading,
    outboundError,
    refetchOutbound,
    
    // Common
    refreshData,
    isLoading: inboundLoading || outboundLoading,
  };
} 